import { useState } from 'react';
import { X, ChevronDown, ChevronRight, Loader2, Check, Image } from 'lucide-react';
import toast from 'react-hot-toast';
import { useFigmaBrowse, useAddFileMapping } from '../../hooks/useFigma';

interface Props {
  projectId: string;
  mappedFileKeys: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}

export default function FigmaFileBrowserModal({ projectId, mappedFileKeys, onClose, onAdded }: Props) {
  const { data, isLoading, error } = useFigmaBrowse(projectId, true);
  const addFileMutation = useAddFileMapping();

  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [selectedFiles, setSelectedFiles] = useState<Map<string, string>>(new Map()); // key → name
  const [isAdding, setIsAdding] = useState(false);

  const toggleProject = (projectId: string) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  const toggleFile = (key: string, name: string) => {
    if (mappedFileKeys.has(key)) return;
    setSelectedFiles((prev) => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else next.set(key, name);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selectedFiles.size === 0) return;
    setIsAdding(true);
    let successCount = 0;

    for (const [fileKey, fileName] of selectedFiles) {
      try {
        await addFileMutation.mutateAsync({ projectId, fileKey, fileName });
        successCount++;
      } catch {
        toast.error(`파일 추가 실패: ${fileName}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount}개 파일이 추가되었습니다.`);
      onAdded();
    }
    setIsAdding(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Figma 파일 탐색</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Loader2 className="size-8 animate-spin mb-3" />
              <p className="text-sm">Figma 파일을 불러오는 중...</p>
              <p className="text-sm text-gray-400 mt-1">Figma API 응답에 시간이 걸릴 수 있습니다.</p>
            </div>
          )}

          {error && (
            <div className="text-center py-8 text-red-500 text-sm">
              파일 목록을 불러오지 못했습니다. Figma 연결 상태를 확인하세요.
            </div>
          )}

          {data && data.projects.length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm">
              팀에 프로젝트가 없습니다.
            </div>
          )}

          {data && data.projects.map((proj) => (
            <div key={proj.id} className="border border-gray-200 rounded-lg mb-3">
              {/* Project header (accordion) */}
              <button
                onClick={() => toggleProject(proj.id)}
                className="flex items-center gap-2 w-full px-4 py-3 hover:bg-gray-50 text-left"
              >
                {expandedProjects.has(proj.id)
                  ? <ChevronDown size={16} className="text-gray-400" />
                  : <ChevronRight size={16} className="text-gray-400" />
                }
                <span className="text-sm font-medium text-gray-900">{proj.name}</span>
                <span className="text-sm text-gray-400">({proj.files.length}개 파일)</span>
              </button>

              {/* File list */}
              {expandedProjects.has(proj.id) && (
                <div className="border-t border-gray-100">
                  {proj.files.map((file) => {
                    const isMapped = mappedFileKeys.has(file.key);
                    const isSelected = selectedFiles.has(file.key);

                    return (
                      <label
                        key={file.key}
                        className={`flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer ${
                          isMapped ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={isSelected || isMapped}
                          disabled={isMapped}
                          onChange={() => toggleFile(file.key, file.name)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />

                        {/* Thumbnail */}
                        <div className="w-10 h-[30px] rounded bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                          {file.thumbnail_url ? (
                            <img src={file.thumbnail_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Image size={14} className="text-gray-400" />
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 truncate">{file.name}</p>
                          <p className="text-sm text-gray-400">
                            {file.last_modified
                              ? new Date(file.last_modified).toLocaleDateString('ko-KR')
                              : ''}
                          </p>
                        </div>

                        {/* Status */}
                        {isMapped && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-sm rounded-full">
                            <Check size={12} />
                            추가됨
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-gray-100">
          <span className="text-sm text-gray-500">
            {selectedFiles.size > 0 ? `${selectedFiles.size}개 파일 선택됨` : '파일을 선택하세요'}
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >
              취소
            </button>
            <button
              onClick={handleAdd}
              disabled={selectedFiles.size === 0 || isAdding}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium
                hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {isAdding && <Loader2 className="size-4 animate-spin" />}
              {isAdding ? '추가 중...' : `선택한 파일 추가 (${selectedFiles.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
